import {Directive, ElementRef, HostListener, inject} from '@angular/core';

@Directive({
  selector: 'details.gw-gatewee-collapsible-details',
  standalone: true,
})
export class GateweeCollapsibleDirective {
  private readonly el = inject(ElementRef<HTMLDetailsElement>);

  private get reducedMotion(): boolean {
    return matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  @HostListener('click', ['$event'])
  onSummaryClick(event: MouseEvent): void {
    if (!(event.target as HTMLElement).closest('summary')) return;

    const details = this.el.nativeElement as HTMLDetailsElement;
    event.preventDefault();

    if (details.open) {
      this.animateClose(details);
    } else {
      this.animateOpen(details);
    }
  }

  private animateOpen(details: HTMLDetailsElement): void {
    const panel = details.querySelector<HTMLElement>(
      '.gw-gatewee-collapsible-panel',
    );

    if (this.reducedMotion || !panel) {
      details.open = true;
      return;
    }

    // Pin panel at 0fr *before* showing so the [open] CSS rule (1fr)
    // doesn't jump straight to 1fr — the inline style acts as the "from" value.
    panel.style.gridTemplateRows = '0fr';
    details.open = true;

    // Force the browser to compute layout with 0fr as the current value.
    void panel.getBoundingClientRect();

    // Remove the inline override — CSS [open] rule (1fr) takes over
    // and the transition 0fr → 1fr plays.
    panel.style.gridTemplateRows = '';
  }

  /** Slightly longer than CSS --gw-collapsible-duration (0.28s) for close path. */
  private static readonly closeFallbackMs = 350;

  private animateClose(details: HTMLDetailsElement): void {
    const panel = details.querySelector<HTMLElement>(
      '.gw-gatewee-collapsible-panel',
    );

    if (this.reducedMotion || !panel) {
      details.open = false;
      return;
    }

    details.classList.add('is-closing');

    let finished = false;
    const finish = () => {
      if (finished) {
        return;
      }
      finished = true;
      window.clearTimeout(fallbackTimer);
      panel.removeEventListener('transitionend', onEnd);
      panel.removeEventListener('transitioncancel', onCancel);
      details.open = false;
      details.classList.remove('is-closing');
    };

    const onEnd = (e: Event) => {
      const te = e as TransitionEvent;
      // Ignore bubbled events from inner elements; only react to the
      // grid-template-rows transition on the panel itself.
      if (te.target !== panel || te.propertyName !== 'grid-template-rows') {
        return;
      }
      finish();
    };

    const onCancel = (e: Event) => {
      const te = e as TransitionEvent;
      if (te.target !== panel || te.propertyName !== 'grid-template-rows') {
        return;
      }
      finish();
    };

    panel.addEventListener('transitionend', onEnd);
    panel.addEventListener('transitioncancel', onCancel);

    // grid-template-rows does not always emit transitionend (browser / containment).
    const fallbackTimer = window.setTimeout(() => {
      finish();
    }, GateweeCollapsibleDirective.closeFallbackMs);
  }
}
